import React, { useContext, useEffect, useState } from 'react';
import Page from '../../../layout/Page';
import { Button, Icon, Modal } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import Table from '../../../app/Table';
import ClientListingPageContext from './ClientListingPageContext';
import ClientColumnRenderer from './ClientColumnRenderer';
import Client from '../../../../models/Client';
import RemovalConfirmationForm from '../../../common/RemovalConfirmationForm';
import DisableConfirmationForm from '../../../common/DisableConfirmationForm';
import User from "../../../../models/User";
import UserRepository from '../../../../common/repository/UserRepository'
import constants from '../../../../common/constants';
import Result from '../../../../common/repository/Result';
import { toast } from '../../../common/Toast';
import LocalStorage from '../../../../lib/LocalStorage';
import config from '../../../../common/repository/config';

interface userRoleDisableBtn {
  user: User | null;
}

const Layout = () => {
  const ctx = useContext(ClientListingPageContext);
  const [open, setOpen] = useState(false);
  const [modalType, setModalType] = useState<'remove' | 'disable' | null>(null);
  const [model, setModel] = useState<Client | null>(null);
  const [users, setUsers] = useState<userRoleDisableBtn>();

  const clientId = model?.getId();

  const openModal = (type: 'remove' | 'disable', model: Client) => {
    setModalType(type);
    setModel(model);
    setOpen(true);
  };

  useEffect(() => {
    const userRepo = new UserRepository();
    (async () => {
      const getUser = await userRepo.getUserByToken();
      if (getUser instanceof Result.Success) {
        setUsers({ user: getUser.data });
      } else {
        toast.error("Unable to find user role.");
      }
    })();
  }, []);

  const importClient = async (clientId: string | undefined) => {
    if (!clientId) {
      toast.error("Client ID not found");
      return;
    }
    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
    if (!token) {
      console.error("Authentication token is missing.");
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseURL}/client/import/caseType`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },
        body: JSON.stringify({ clientId }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || "Imported successfully!");
      } else {
        toast.error(data.message || "Something went wrong!");
      }
      console.log(data);
    } catch (error) {
      toast.error("Error occurred while fetching data");
    }
  };


  const removePrompt = async () => {
    const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
    if (!token) {
      console.error("Authentication token is missing.");
      return;
    }
    try {
      const response = await fetch(`${config.apiBaseURL}/client/delete/prompt`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": token,
        },

      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message || "Prompt removed  successfully!");
      } else {
        toast.error(data.message || "Something went wrong!");
      }
      console.log(data);
    } catch (error) {
      toast.error("Error occurred while fetching data");
    }
  };

  const closeModal = () => {
    setOpen(false);
    setModel(null);
    setModalType(null);
  };

  const handleRemoveConfirm = async () => {
    await ctx.remove(model!);
    await ctx.load();
    closeModal();
  };

  const handleDisableConfirm = async () => {
    await ctx.disable(model!);
    closeModal();
  };

  const columnRenderer = new ClientColumnRenderer(
    (model: Client) => {
      const role = users?.user?.getRole().getTitle();
      const canShowDisable =
        role === constants.roleTitle.ceo ||
        role === constants.roleTitle.md;

      return (
        <div style={{ minWidth: "320px", whiteSpace: "nowrap" }}>
          <Button.Group size="mini">
            <Button
              style={{
                marginRight: canShowDisable ? "7px" : "",
                borderRadius: canShowDisable ? "5px" : ""
              }}
              as={Link}
              to={`/master/client/update/${model.getId()}`}
            >
              Open
            </Button>

            <Button
              style={{
                marginRight: canShowDisable ? "7px" : "",
                borderRadius: canShowDisable ? "5px" : ""
              }}
              onClick={() => openModal('remove', model)}
              negative
            >
              Remove
            </Button>

            <Button onClick={() => importClient(model.getId())} style={{ marginRight: "10px", borderRadius: "4px", color: "white", border: "1px solid", backgroundColor: "#2d44d6fc" }} >
              Import
            </Button>

            {canShowDisable && (
              <Button
                style={{ borderRadius: "5px" }}
                onClick={() => openModal('disable', model)}
                positive={model.getdisable ? model.getdisable() === true : true}
                negative={model.getdisable ? model.getdisable() === false : true}
              >
                {model.getdisable() ? "Enable" : "Disable"}
              </Button>
            )}
          </Button.Group>
        </div>
      );

    },
    (obj: any) => obj.name !== undefined
  );

  return (
    <Page title="Clients" description="View clients">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button icon labelPosition="left" color="green" as={Link} to="/master/client/add">
          <Icon name="add" />
          Create New
        </Button>
        <Button
  style={{
    backgroundColor: "#ee2f12ff ", // your red hex
    color: "#fff"
  }}          size="small"
          onClick={removePrompt}
        >
           <Icon name="trash" />
          Remove Prompt
        </Button>
      </div>
      <Modal open={open} onClose={closeModal} size="mini" closeOnDimmerClick={false} closeOnEscape={false}>
        <Modal.Header>Confirmation</Modal.Header>
        <Modal.Content>
          {modalType === 'remove' ? (
            <RemovalConfirmationForm
              title="Are you sure you want to remove this?"
              remove={handleRemoveConfirm}
              cancel={closeModal}
            />
          ) : (
            <DisableConfirmationForm
              title={`Are you sure you want to ${model?.getdisable() ? "enable" : "disable"} this client?`}
              actionText={model?.getdisable() ? "Enable" : "Disable"}
              disable={handleDisableConfirm}
              cancel={closeModal}
            />
          )}
        </Modal.Content>
      </Modal>
      <Table
        renderer={columnRenderer}
        data={ctx.clients}
        totalDataCount={ctx.count}
        loading={ctx.loading}
        load={ctx.load}
        onRowsPerPageChange={(page, rowsPerPage) => {
          const offset = (page - 1) * rowsPerPage;
          const limit = offset + rowsPerPage;
          ctx.setLimit(limit);
          ctx.setOffset(offset);
          ctx.load();
        }}
        onSearch={(text) => {
          ctx.setFilter(text);
          ctx.setOffset(0);
          ctx.setCount(0);
          ctx.load();
        }}
        onClear={() => {
          ctx.setFilter('');
          ctx.setOffset(0);
          ctx.setCount(0);
          ctx.load();
        }}
      />
    </Page>
  );
};

export default Layout;
